import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { fError } from '@/utils/format-error'
import { Button } from '@/components/ui/button'
import axiosInstance, { endpoints } from '@/lib/axios'
import {
  type RoosterMatch,
  RoosterMatchStatus,
} from '@/types/rooster-match.type'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { getStatusLabel } from './helper'

interface RoosterMatchStatusDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  match: RoosterMatch | null
  onSaved?: () => void
}

export default function RoosterMatchStatusDialog({
  open,
  onOpenChange,
  match,
  onSaved,
}: RoosterMatchStatusDialogProps) {
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState<string>(match?.status || '')

  useEffect(() => {
    setStatus(match?.status || '')
  }, [match])

  const handleSave = async () => {
    if (!match || !status || saving) return
    try {
      setSaving(true)
      await axiosInstance.patch(endpoints.roosterMatches.update(match.id), {
        status,
      })
      toast.success('อัปเดตสถานะการแข่งขันสำเร็จ')
      onSaved?.()
      onOpenChange(false)
    } catch (error) {
      toast.error(fError(error))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>ปรับสถานะการแข่งขัน</DialogTitle>
          <DialogDescription>
            เลือกสถานะใหม่สำหรับการแข่งขันที่เลือก
          </DialogDescription>
        </DialogHeader>

        {match && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              ปรับสถานะการแข่งขันหมายเลข #{match.matchNumber || '-'}
            </p>
            <div className="space-y-1">
              <label className="text-sm font-medium text-black">สถานะ</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="min-w-[220px]">
                  <SelectValue placeholder="เลือกสถานะ" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(RoosterMatchStatus).map((s) => (
                    <SelectItem key={s} value={s}>
                      {getStatusLabel(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            ยกเลิก
          </Button>
          <Button onClick={handleSave} disabled={saving || !status}>
            บันทึก
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
